<template>
  <div :key="componentKey">
    <VueSelect
      :id="id"
      v-model="innerValue"
      :options="normalizedOptions"
      :is-clearable="false"
      :class="['form-select', { 'form-select-error': showError }]"
      :required="required"
      :is-disabled="isDisabled"
      :multiple="false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import VueSelect from 'vue3-select-component'
import type { Option } from 'vue3-select-component'
import { updateComponentKey } from '../../composables/ComponentKey'

const props = withDefaults(
  defineProps<{
    id: string
    options?: string[]
    required?: boolean
    showError?: boolean
    modelValue: string | null
    isDisabled: boolean
  }>(),
  {
    showError: false,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
}>()

const normalizedOptions = computed<Option<string | null>[]>(() =>
  (props.options ?? []).map(o => ({ label: o, value: o }))
)

const innerValue = computed<string | null>({
  get: () => props.modelValue ?? null,
  set: val => emit('update:modelValue', val),
})

let componentKey = updateComponentKey(props)
</script>

<style scoped>
.form-select {
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  --vs-max-height: 19px;
  --vs-margin-bottom: 10px;
  --vs-line-height: normal;

  --vs-padding: 12px 12px;
  --vs-width: 100%;
  --vs-min-height: 18px;
  --vs-border-radius: 8px;
  --vs-border: 1px solid var(--border-color);
  --vs-font-size: 14px;
  --vs-font-weight: 500;
  --vs-text-color: var(--text-color);
  --vs-placeholder-color: var(--light-color);

  --vs-background-color: var(--background-color);
  --vs-disabled-background-color: var(--background-color);

  --vs-outline-width: 1px;
  --vs-outline-color: var(--border-color);

  --vs-menu-background-color: var(--background-color);
  --vs-menu-border: 1px solid var(--border-color);
  --vs-menu-box-shadow: none;

  --vs-option-text-color: var(--text-color);
  --vs-option-hover-text-color: var(--text-color);
  --vs-option-selected-text-color: var(--text-color);
  --vs-option-focused-text-color: var(--text-color);

  --vs-option-background-color: var(--background-color);
  --vs-option-hover-background-color: var(--hover-background-color);
  --vs-option-focused-background-color: var(--hover-background-color);
  --vs-option-selected-background-color: var(--hover-background-color);

  --vs-indicator-icon-color: var(--text-color);
}

.form-select-error {
  --vs-border: 1px solid #e32c57;
  --vs-outline-color: #e32c57;
}

:deep(.vue-select .search-input:focus) {
  outline: none;
  box-shadow: none;
}
</style>
